import { useState, useEffect, useRef } from "react";
import { api, ApiError } from "../lib/api";

interface Props { onDone: () => void }

export default function Login({ onDone }: Props) {
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const codeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api<{ requiresPassword: boolean }>("/api/auth/status", { timeoutMs: 15_000 }).then((s) => {
      setRequiresPassword(s.requiresPassword);
    }).catch(() => {});
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/api/auth/login", {
        method: "POST",
        timeoutMs: 20_000,
        // The server matches the address case-insensitively; trim here so a space pasted
        // along with it can't cause a rejection with no visible reason.
        body: JSON.stringify({
          email: email.trim(),
          password: password || undefined,
          code: code.trim(),
        }),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Login failed");
      // An authenticator code lasts ~30 seconds and can't be reused, so clear it and put the
      // cursor back — resubmitting the same one would just fail again.
      setCode("");
      codeRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={submit} className="card w-full max-w-sm space-y-4 p-6">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-slate-800 to-slate-950 text-[12px] font-bold text-white shadow-soft">M</div>
            <div className="text-base font-semibold tracking-tightish">MailBIT Tools</div>
          </div>
          <div className="mt-3 text-lg font-semibold tracking-tightish">Sign in</div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Email</label>
          <input type="email" autoComplete="username" autoFocus
                 value={email} onChange={(e) => setEmail(e.target.value)}
                 className="w-full" placeholder="you@mailbit.co.th" required />
        </div>
        {requiresPassword && (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Password</label>
            <input type="password" autoComplete="current-password" value={password}
                   onChange={(e) => setPassword(e.target.value)} className="w-full" required />
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Authenticator code (or recovery code)
          </label>
          <input
            ref={codeRef}
            type="text" inputMode="text" autoComplete="one-time-code"
            value={code} onChange={(e) => setCode(e.target.value)} className="w-full"
            placeholder="123456 or XXXX-XXXX-XXXX" required
          />
        </div>
        {error && (
          <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">
            <div>{error}</div>
            <div className="mt-1 text-xs text-rose-600/90">
              Codes expire every 30 seconds — enter the current one from your authenticator app.
              If they keep failing, check your phone's clock is set automatically, or use a recovery code.
            </div>
          </div>
        )}
        <button type="submit" disabled={submitting} className="btn-primary w-full">
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
